import { t } from '@trimble/i18n';
import { Component, createMemo, onMount, Show } from 'solid-js';
import { ColorPicker } from '../color-picker/ColorPicker';
import { BasicButton } from '../toolbar/toolbar-controls/BasicButton';
import { TrbToolbar } from '../toolbar/TrbToolbar';
import { ControlDropdown } from '../control-dropdown/ControlDropdown';
import type { ControlMenuItemType } from '../control-menu';
import tinycolor from 'tinycolor2';

export interface ObjectSelectionToolbarColorPickerProps {
  open: boolean;
  onToggle(open: boolean): void;
  initialColor?: { r: number; g: number; b: number };
  initialOpacity?: number;
  /** Live color updates while the ColorPicker is open (ColorPicker.onChange). */
  onChange?(color: { r: number; g: number; b: number }): void;
  /** Color commit when the user saves/confirms (ColorPicker.onSave). */
  onSave?(color: { r: number; g: number; b: number }): void;
  onOpacityChange?(opacity: number): void;
  onReset?(): void;
  useTransparency?: boolean;
  showResetButton?: boolean;
  suppressColorSelection?: boolean;
}

export interface ObjectSelectionToolbarVisibilityProps {
  onHideSelection?(): void;
  onShowOnlySelected?(): void;
  onShowAll?(): void;
  isHideSelectionActive?: boolean;
  isShowOnlySelectedActive?: boolean;
  onIsolateLayers?(): void;
  onUnisolateLayers?(): void;
  /** Civil simple eye toggle (mutually exclusive with hide/show-only pair in practice). */
  onToggleVisibility?(): void;
  isHidden?: boolean;
}

export interface ObjectSelectionToolbarSurfaceProps {
  onCreate(): void;
  tooltip?: string;
  isPending?: boolean;
}

export interface ObjectSelectionToolbarPropertiesProps {
  onToggle(): void;
  isActive?: boolean;
}

export interface ObjectSelectionToolbarProps {
  selectionCount: number;
  customClasses?: string;
  /** If true, the toolbar will be rendered in dark mode regardless of the system theme. Default is false.*/
  darkMode?: boolean;
  onFit?(): void;
  onClip?(): void;
  onDelete?(): void;
  colorPicker?: ObjectSelectionToolbarColorPickerProps;
  visibility?: ObjectSelectionToolbarVisibilityProps;
  surface?: ObjectSelectionToolbarSurfaceProps;
  properties?: ObjectSelectionToolbarPropertiesProps;
}

export const ObjectSelectionToolbar: Component<ObjectSelectionToolbarProps> = (
  props
) => {
  const selectionLabel = createMemo((): string => {
    return t('objectSelectionToolbar.selected', {
      count: props.selectionCount,
    });
  });

  const initialColorHex = createMemo(() => {
    const initialColor = props.colorPicker?.initialColor;
    return initialColor ? tinycolor(initialColor).toHexString() : undefined;
  });

  const changeVisibilityItems = createMemo((): ControlMenuItemType[] => {
    const items: ControlMenuItemType[] = [];
    const visibility = props.visibility;

    const onShowAll = visibility?.onShowAll;
    if (onShowAll) {
      items.push({
        id: 'showAll',
        type: 'action',
        label: t('objectSelectionToolbar.showAll'),
        onClick: () => onShowAll(),
      });
    }

    const onIsolateLayers = visibility?.onIsolateLayers;
    const onUnisolateLayers = visibility?.onUnisolateLayers;

    if (onShowAll && (onIsolateLayers || onUnisolateLayers)) {
      items.push({
        id: 'divider',
        type: 'divider',
      });
    }

    if (onIsolateLayers) {
      items.push({
        id: 'isolateLayers',
        type: 'action',
        label: t('objectSelectionToolbar.isolateLayers'),
        onClick: () => onIsolateLayers(),
      });
    }

    if (onUnisolateLayers) {
      items.push({
        id: 'unisolateLayers',
        type: 'action',
        label: t('objectSelectionToolbar.unisolateLayers'),
        onClick: () => onUnisolateLayers(),
      });
    }

    return items;
  });

  // Override to enable dark mode for the toolbar. Colors are copied from tcweb.
  let wrapper: HTMLDivElement | undefined;

  const darkModeThemeOverride = (): void => {
    const styleId = 'object-select-dark-theme';
    // Styles in document.head do not reach elements inside a shadow root, so
    // the override goes into whichever root the toolbar is rendered in.
    const root = wrapper?.getRootNode();
    const styleHost: ParentNode =
      root instanceof ShadowRoot ? root : document.head;
    if (!styleHost.querySelector(`#${styleId}`)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .force-dark-theme {
          --modus-wc-color-base-page: #363545;
          --modus-wc-color-base-content: #ffffff;

          /* Button adds .active on hover, and the tcweb stylesheet greys
             .button.tertiary.active > i with !important. Adopted stylesheets
             cascade after this one, so these selectors must out-specify it. */
          & .dropdown-container button,
          & .button.tertiary,
          & .button.tertiary.active > i,
          & .button.tertiary:active > i
          {
            color: #ffffff !important;
          }

          & .dropdown-container button:hover,
          & .dropdown-container button:active,
          & .button.tertiary:hover,
          & .button.tertiary:active
          {
            background-color: #6A6976;
          }

          & .button.active
          {
            background-color: #8c8b96 !important;

            & > i
            {
              color: #ffffff !important;
            }
          }
        }
      `;
      styleHost.appendChild(style);
    }
  };

  onMount(darkModeThemeOverride);

  return (
    <div
      ref={wrapper}
      class={(props.darkMode ?? false) ? 'force-dark-theme' : ''}
    >
      <TrbToolbar
        orientation="horizontal"
        addSeparators={false}
        customClasses={props.customClasses}
      >
        <div
          class="flex items-center gap-1"
          data-testid="object-selection-toolbar"
        >
          <span
            class="text-sm text-base-content whitespace-nowrap px-1"
            data-testid="object-selection-toolbar-count"
          >
            {selectionLabel()}
          </span>
          <Show when={props.surface} keyed>
            {(surface) => (
              <Show
                when={!surface.isPending}
                fallback={
                  <div
                    class="w-10 h-10 flex items-center justify-center"
                    data-testid="object-selection-toolbar-create-surface-busy"
                    role="status"
                    title={t('general.loading')}
                  >
                    {/* eslint-disable-next-line custom/no-modus-v1 -- TWENG-132 */}
                    <modus-spinner class="m-auto" color="primary" size="20px" />
                  </div>
                }
              >
                <span data-testid="object-selection-toolbar-create-surface">
                  <BasicButton
                    iconName="svg-surface"
                    tooltip={
                      surface.tooltip ??
                      t('objectSelectionToolbar.createSurfaceTooltip')
                    }
                    action={surface.onCreate}
                    size="medium"
                  />
                </span>
              </Show>
            )}
          </Show>
          <Show when={props.properties} keyed>
            {(properties) => (
              <span data-testid="object-selection-toolbar-properties">
                <BasicButton
                  iconName="tc-icon-info"
                  tooltip={t('objectSelectionToolbar.propertiesPanelTooltip')}
                  action={properties.onToggle}
                  size="medium"
                  pressed={properties.isActive ?? false}
                />
              </span>
            )}
          </Show>
          <Show when={props.onFit} keyed>
            {(fit) => (
              <span data-testid="object-selection-toolbar-fit">
                <BasicButton
                  iconName="tc-icon-view-fit"
                  tooltip={t('objectSelectionToolbar.fitToSelectionTooltip')}
                  action={fit}
                  size="medium"
                />
              </span>
            )}
          </Show>
          <Show when={props.onClip} keyed>
            {(clip) => (
              <span data-testid="object-selection-toolbar-crop">
                <BasicButton
                  iconName="svg-clip-box"
                  tooltip={t('objectSelectionToolbar.clipBoxTooltip')}
                  action={clip}
                  size="medium"
                />
              </span>
            )}
          </Show>
          <Show
            when={
              props.colorPicker &&
              (props.colorPicker.onChange || props.colorPicker.onSave)
            }
          >
            <div
              data-testid="object-selection-toolbar-color-picker"
              class="relative"
            >
              <BasicButton
                iconName="paint_bucket"
                tooltip={t('objectSelectionToolbar.colorPickerTooltip')}
                action={() =>
                  props.colorPicker?.onToggle(!props.colorPicker.open)
                }
                size="medium"
                pressed={props.colorPicker?.open}
              />
              <Show when={props.colorPicker?.open}>
                <ColorPicker
                  useTransparency={props.colorPicker?.useTransparency ?? true}
                  showResetButton={props.colorPicker?.showResetButton ?? true}
                  suppressSelection={props.colorPicker?.suppressColorSelection}
                  initialColor={initialColorHex()}
                  initialTransparency={
                    1 - (props.colorPicker?.initialOpacity ?? 1)
                  }
                  onChange={(rgb) => {
                    props.colorPicker?.onChange?.(rgb);
                  }}
                  onSave={(rgb) => {
                    props.colorPicker?.onSave?.(rgb);
                  }}
                  onTransparencyChange={(transparency) => {
                    props.colorPicker?.onOpacityChange?.(1 - transparency);
                  }}
                  onReset={() => {
                    props.colorPicker?.onReset?.();
                  }}
                />
              </Show>
            </div>
          </Show>
          <Show when={props.visibility?.onHideSelection} keyed>
            {(hideSelection) => (
              <span data-testid="object-selection-toolbar-hide-selection">
                <BasicButton
                  iconName="tc-icon-eye-visibility-off"
                  tooltip={t('objectSelectionToolbar.hideSelectionTooltip')}
                  action={hideSelection}
                  size="medium"
                  pressed={props.visibility?.isHideSelectionActive}
                />
              </span>
            )}
          </Show>
          <Show when={props.visibility?.onShowOnlySelected} keyed>
            {(showOnlySelected) => (
              <span data-testid="object-selection-toolbar-show-only-selected">
                <BasicButton
                  iconName="tc-icon-hide-others"
                  tooltip={t('objectSelectionToolbar.showOnlySelectedTooltip')}
                  action={showOnlySelected}
                  size="medium"
                  pressed={props.visibility?.isShowOnlySelectedActive}
                />
              </span>
            )}
          </Show>
          <Show when={props.visibility?.onToggleVisibility} keyed>
            {(toggleVisibility) => (
              <span data-testid="object-selection-toolbar-toggle-visibility">
                <BasicButton
                  iconName="tc-icon-eye-visibility-off"
                  tooltip={
                    props.visibility?.isHidden
                      ? t('objectSelectionToolbar.showSelectionTooltip')
                      : t('objectSelectionToolbar.hideSelectionTooltip')
                  }
                  action={toggleVisibility}
                  size="medium"
                  pressed={props.visibility?.isHidden}
                />
              </span>
            )}
          </Show>
          <Show when={changeVisibilityItems().length > 0 || props.onDelete}>
            <span class="w-[1px] h-10 bg-white" />
            <Show when={changeVisibilityItems().length > 0}>
              <span
                data-testid="object-selection-toolbar-change-visibility"
                class="dropdown-container"
              >
                <ControlDropdown
                  anchorIcon={
                    <div class="whitespace-nowrap leading-10">
                      {t('objectSelectionToolbar.changeVisibility')}
                    </div>
                  }
                  items={changeVisibilityItems()}
                  position="right"
                />
              </span>
            </Show>
            <Show when={props.onDelete} keyed>
              {(onDelete) => (
                <span data-testid="object-selection-toolbar-delete">
                  <BasicButton
                    iconName="delete"
                    tooltip={t('objectSelectionToolbar.deleteTooltip')}
                    action={onDelete}
                    size="medium"
                  />
                </span>
              )}
            </Show>
          </Show>
        </div>
      </TrbToolbar>
    </div>
  );
};
